import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Address } from '@/components/Cards/AddressCard/Address/Address';
import { useParentHierarchyNodes } from '@/components/Hco/_hooks/useParentHierarchyNodes';
import { ParentHierarchyBreadcrumb } from '@/components/Hco/ParentHierarchyBreadcrumb/ParentHierarchyBreadcrumb';
import { AffiliationAddress } from '@/components/Hcp/AffiliationAddress/AffiliationAddress';
import { useProfileMetaData } from '@/components/Hcp/HcpCard/_hooks/useProfileMetaData';
import { ProfileName } from '@/components/Hcp/ProfileName/ProfileName';
import { TEST_IDS } from '@/core/constants/app';
import type { Hco, Hcphco } from '@/core/graphql/generated/types';
import { useCopyToClipboard } from '@/utils/copyToClipboard';
import { formatLocalizedName } from '@/utils/formatters/formatLocalizedName';

import { ProfilePageHeader } from '../ProfilePageHeader/ProfilePageHeader';

import { hcpMetaData as sx } from './ProfileMetaData.styles';

type Props = {
	profile: Hcphco | null;
	withoutHeader?: boolean;
	withoutAddress?: boolean;
	withoutFooter?: boolean;
	withoutCountry?: boolean;
	withProfileLink?: boolean;
	withInactive?: boolean;
	withInternationalName?: boolean;
	withLink?: boolean;
	withBreadcrumb?: boolean;
	lineClamp?: number;
	inline?: boolean;
	withShortName?: boolean;
	withNickname?: boolean;
	withAffiliation?: boolean;
	affiliationOverride?: Hco | null;
	chips?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		nameInfoContainer?: SxProps<Theme>;
		name?: SxProps<Theme>;
		nameRoot?: SxProps<Theme>;
	};
};

export const ProfileMetaData = ({
	profile,
	withoutHeader = false,
	withoutAddress = false,
	withoutFooter = false,
	withoutCountry = false,
	withProfileLink = false,
	withInactive = false,
	withInternationalName = false,
	withLink = false,
	withBreadcrumb = false,
	inline = false,
	lineClamp,
	withShortName = false,
	withNickname = false,
	withAffiliation = false,
	affiliationOverride,
	chips,
	customSx
}: Props) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();
	const {
		specialtyAndType,
		primaryAffiliation: defaultPrimaryAffiliation,
		primaryAddress
	} = useProfileMetaData(profile);

	const hierarchyNodes = useParentHierarchyNodes(profile);
	const breadcrumbNodes = useMemo(
		() => hierarchyNodes.filter(node => !node.isCurrent),
		[hierarchyNodes]
	);

	const primaryAffiliation =
		affiliationOverride !== undefined
			? affiliationOverride
			: defaultPrimaryAffiliation;

	const addressView = profile?.__typename === 'HCO' ? 'summary' : 'basic';
	const affiliationToShow =
		withAffiliation && profile?.__typename === 'HCP'
			? primaryAffiliation
			: null;

	const showHeader = !withoutHeader;
	const status = profile?.statusCda_V;
	const statusReason =
		profile?.__typename === 'HCP'
			? formatLocalizedName(profile.hcpReasonStatus)
			: undefined;
	const statusLine = [status, statusReason].filter(Boolean).join(' | ');

	const veevaIdNode = profile?.veevaid_V && (
		<Box sx={sx.veevaIdContainer} data-testid={TEST_IDS.profile.veevaId}>
			<Typography variant='body2' sx={sx.veevaIdLabel}>
				{t('Veeva ID')}: {profile.veevaid_V}
			</Typography>
			<IconButton
				size='small'
				onClick={() => copyToClipboard(profile.veevaid_V)}
				sx={sx.copyButton}
			>
				<ContentCopyIcon sx={sx.copyIcon} />
			</IconButton>
		</Box>
	);

	const nameBlock = (
		<ProfileName
			profile={profile}
			withDegrees
			chips={chips}
			shouldValidateMarkup={false}
			withProfileLink={withProfileLink}
			withInactive={withInactive}
			withInternationalName={withInternationalName}
			withLink={withLink}
			inline={inline}
			lineClamp={lineClamp}
			withShortName={withShortName}
			withNickname={withNickname}
			customSx={{
				name: [sx.name, customSx?.name ?? false] as SxProps<Theme>,
				root: customSx?.nameRoot
			}}
		/>
	);

	const breadcrumb = withBreadcrumb && breadcrumbNodes.length > 0 && (
		<Box sx={sx.breadcrumbVisibility}>
			<ParentHierarchyBreadcrumb nodes={breadcrumbNodes} />
		</Box>
	);

	const leftDetails = (
		<>
			<Typography>{specialtyAndType}</Typography>

			{profile?.__typename === 'HCO' ? (
				!withoutAddress &&
				primaryAddress && (
					<Address
						address={primaryAddress}
						view={addressView}
						inline
						withoutCountry={withoutCountry}
					/>
				)
			) : (
				<AffiliationAddress
					primaryAffiliation={affiliationToShow}
					addressView={addressView}
					withoutCountry={withoutCountry}
				/>
			)}
		</>
	);

	if (!showHeader) {
		return (
			<Box sx={[sx.root, customSx?.root ?? false] as SxProps<Theme>}>
				<Box
					sx={
						[
							sx.nameInfoContainer,
							customSx?.nameInfoContainer ?? false
						] as SxProps<Theme>
					}
				>
					{breadcrumb}
					{nameBlock}
					{leftDetails}
					{!withoutFooter && veevaIdNode}
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={[sx.root, customSx?.root ?? false] as SxProps<Theme>}>
			<Box sx={sx.headerGrid}>
				<ProfilePageHeader profile={profile} />

				{breadcrumb && <Box sx={sx.breadcrumbCell}>{breadcrumb}</Box>}

				<Box
					sx={
						[
							sx.nameCell,
							customSx?.nameInfoContainer ?? false
						] as SxProps<Theme>
					}
				>
					{nameBlock}
				</Box>
			</Box>

			<Box sx={sx.bodyRow}>
				<Box sx={sx.leftColumn}>{leftDetails}</Box>

				<Box sx={sx.rightColumn}>
					{statusLine && (
						<Typography variant='body2' sx={sx.statusLabel}>
							{statusLine}
						</Typography>
					)}
					{!withoutFooter && veevaIdNode}
				</Box>
			</Box>
		</Box>
	);
};
